import {
  createProduct as createProductApi,
  deleteProduct as deleteProductApi,
  getProduct as getProductApi,
  getProductUsage as getProductUsageApi,
  listProductProblemLinks as listProductProblemLinksApi,
  upsertProductProblemLink as upsertProductProblemLinkApi,
  deleteProductProblemLink as deleteProductProblemLinkApi,
  listProducts as listProductsApi,
  patchProduct as patchProductApi,
  reorderProducts as reorderProductsApi,
} from '@shared/api/products';
import { type Paged, parsePaged } from '@shared/api/paging';
import type {
  ProductCreateRequest,
  ProductListQuery,
  ProductPatchRequest,
  ProductUsageDetail,
} from '@shared/types/product';
import type {
  ProductProblemLink,
  ProductProblemLinkUpsertRequest,
} from '@shared/types/product-problem-link';
import type { Product } from '@domain/product/model';
import { parseProduct } from '@domain/product/parseProduct';

interface ListState {
  data: Paged<Product> | null;
  loading: boolean;
  error: string | null;
}

const listState = $state<ListState>({
  data: null,
  loading: false,
  error: null,
});
let listToken = 0;

function applyLocalProductOrder(productIds: readonly string[]): void {
  if (!listState.data) return;
  const rank = new Map(productIds.map((id, index) => [id, index]));
  const items = [...listState.data.items]
    .sort(
      (a, b) =>
        (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    )
    .map((product, index) => ({ ...product, sort_order: index }));
  listState.data = { ...listState.data, items };
}

export const productListStore = {
  get data(): Paged<Product> | null {
    return listState.data;
  },
  get loading(): boolean {
    return listState.loading;
  },
  get error(): string | null {
    return listState.error;
  },
  async refresh(query: ProductListQuery): Promise<void> {
    const token = ++listToken;
    listState.data = null;
    listState.loading = true;
    listState.error = null;
    try {
      const raw = await listProductsApi(query);
      if (token !== listToken) return;
      listState.data = parsePaged(raw, parseProduct);
    } catch (err) {
      if (token !== listToken) return;
      listState.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === listToken) listState.loading = false;
    }
  },
  reorderLocal(productIds: readonly string[]): void {
    applyLocalProductOrder(productIds);
  },
};

interface DetailState {
  data: Product | null;
  loading: boolean;
  error: string | null;
}

const detailState = $state<DetailState>({
  data: null,
  loading: false,
  error: null,
});
let detailToken = 0;

export const productDetailStore = {
  get data(): Product | null {
    return detailState.data;
  },
  get loading(): boolean {
    return detailState.loading;
  },
  get error(): string | null {
    return detailState.error;
  },
  async load(id: string): Promise<void> {
    const token = ++detailToken;
    detailState.loading = true;
    detailState.error = null;
    try {
      const raw = await getProductApi(id);
      if (token !== detailToken) return;
      detailState.data = parseProduct(raw);
    } catch (err) {
      if (token !== detailToken) return;
      detailState.error = err instanceof Error ? err.message : String(err);
      detailState.data = null;
    } finally {
      if (token === detailToken) detailState.loading = false;
    }
  },
  setLocal(p: Product): void {
    detailState.data = p;
  },
  clear(): void {
    detailToken++;
    detailState.data = null;
    detailState.error = null;
    detailState.loading = false;
  },
};

/// 商品マスタを「選択肢 / id→name 解決」用に全件ロードするストア。
///
/// 契約一覧 (deals) と営業アクション (sales) で product_id を名前表示するため、詳細
/// ページを開いた時点で 1 回ロードしておく。一覧ページ用の `productListStore` は
/// ページネーション付きで都度上書きされるため、解決用途とは別インスタンスにする。
interface OptionsState {
  data: Product[];
  loading: boolean;
  error: string | null;
}

const optionsState = $state<OptionsState>({
  data: [],
  loading: false,
  error: null,
});
let optionsToken = 0;

export const productOptionsStore = {
  get data(): Product[] {
    return optionsState.data;
  },
  get loading(): boolean {
    return optionsState.loading;
  },
  get error(): string | null {
    return optionsState.error;
  },
  /// 商品マスタを name 昇順で全件 (最大 500) ロードする。stale は token で破棄。
  async load(): Promise<void> {
    const token = ++optionsToken;
    optionsState.loading = true;
    optionsState.error = null;
    try {
      const raw = await listProductsApi({
        page: 1,
        page_size: 500,
        sort_by: 'name',
        sort_dir: 'asc',
      });
      if (token !== optionsToken) return;
      optionsState.data = parsePaged(raw, parseProduct).items;
    } catch (err) {
      if (token !== optionsToken) return;
      optionsState.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === optionsToken) optionsState.loading = false;
    }
  },
  clear(): void {
    optionsToken++;
    optionsState.data = [];
    optionsState.loading = false;
    optionsState.error = null;
  },
};

/// `GET /api/master/products/{id}/usage` の結果をキャッシュするストア。
///
/// ProductDetailPage の「利用状況」タブ表示に使う read-only ストア。
/// `ProductUsageDetail` は typed API 型をそのまま保持 (parse-don't-validate 対象外)。
interface UsageState {
  data: ProductUsageDetail | null;
  loading: boolean;
  error: string | null;
}

const usageState = $state<UsageState>({
  data: null,
  loading: false,
  error: null,
});
let usageToken = 0;

export const productUsageStore = {
  get data(): ProductUsageDetail | null {
    return usageState.data;
  },
  get loading(): boolean {
    return usageState.loading;
  },
  get error(): string | null {
    return usageState.error;
  },
  /// 指定商材の利用状況を取得する。stale は token で破棄。
  async load(id: string): Promise<void> {
    const token = ++usageToken;
    usageState.loading = true;
    usageState.error = null;
    try {
      const data = await getProductUsageApi(id);
      if (token !== usageToken) return;
      usageState.data = data;
    } catch (err) {
      if (token !== usageToken) return;
      usageState.error = err instanceof Error ? err.message : String(err);
      usageState.data = null;
    } finally {
      if (token === usageToken) usageState.loading = false;
    }
  },
  clear(): void {
    usageToken++;
    usageState.data = null;
    usageState.error = null;
    usageState.loading = false;
  },
};

/// `product_problem_links` を保持するストア。
///
/// ProductDetailPage の「解決する課題」タブで使う。
/// upsert / delete mutation は成功後に store state を直接更新する (再 fetch 不要)。
interface ProblemLinksState {
  data: ProductProblemLink[];
  loading: boolean;
  error: string | null;
}

const problemLinksState = $state<ProblemLinksState>({
  data: [],
  loading: false,
  error: null,
});
let problemLinksToken = 0;

export const productProblemLinksStore = {
  get data(): ProductProblemLink[] {
    return problemLinksState.data;
  },
  get loading(): boolean {
    return problemLinksState.loading;
  },
  get error(): string | null {
    return problemLinksState.error;
  },
  /// 指定商材の課題リンク一覧を取得する。stale は token で破棄。
  async load(productId: string): Promise<void> {
    const token = ++problemLinksToken;
    problemLinksState.loading = true;
    problemLinksState.error = null;
    try {
      const res = await listProductProblemLinksApi(productId);
      if (token !== problemLinksToken) return;
      problemLinksState.data = res.items;
    } catch (err) {
      if (token !== problemLinksToken) return;
      problemLinksState.error =
        err instanceof Error ? err.message : String(err);
    } finally {
      if (token === problemLinksToken) problemLinksState.loading = false;
    }
  },
  /// 課題リンクを upsert する。成功後に store state を楽観更新。
  async upsert(
    productId: string,
    problemId: string,
    body: ProductProblemLinkUpsertRequest,
  ): Promise<ProductProblemLink> {
    const updated = await upsertProductProblemLinkApi(
      productId,
      problemId,
      body,
    );
    problemLinksState.data = [
      updated,
      ...problemLinksState.data.filter(
        (l) => l.problem_id !== updated.problem_id,
      ),
    ];
    return updated;
  },
  /// 課題リンクを削除する。成功後に store state から除外。
  async delete(productId: string, problemId: string): Promise<void> {
    await deleteProductProblemLinkApi(productId, problemId);
    problemLinksState.data = problemLinksState.data.filter(
      (l) => l.problem_id !== problemId,
    );
  },
  /// 外部から渡ってきたリンクを store state に反映する。
  /// AddProblemLinkModal 等、store.upsert() を経由しない経路のリンク追加に使う。
  addOrReplace(link: ProductProblemLink): void {
    problemLinksState.data = [
      link,
      ...problemLinksState.data.filter((l) => l.problem_id !== link.problem_id),
    ];
  },
  clear(): void {
    problemLinksToken++;
    problemLinksState.data = [];
    problemLinksState.error = null;
    problemLinksState.loading = false;
  },
};

export async function createProduct(
  body: ProductCreateRequest,
): Promise<Product> {
  return parseProduct(await createProductApi(body));
}

export async function patchProduct(
  id: string,
  body: ProductPatchRequest,
): Promise<Product> {
  const updated = parseProduct(await patchProductApi(id, body));
  if (detailState.data?.id === id) detailState.data = updated;
  return updated;
}

export async function reorderProducts(productIds: string[]): Promise<void> {
  await reorderProductsApi({ product_ids: productIds });
  applyLocalProductOrder(productIds);
}

export async function deleteProduct(id: string): Promise<void> {
  await deleteProductApi(id);
  if (detailState.data?.id === id) detailState.data = null;
}
